import { useEffect, useState } from "react";
import { THEME_STORAGE_KEY, ThemeMode, applyThemeMode, getInitialThemeMode, getNextThemeMode } from "../utils/theme";
import { useI18n } from "../i18n/i18n";

type ThemeSwitcherProps = {
  className?: string;
};

const ThemeIcon = ({ mode }: { mode: ThemeMode }) => {
  if (mode === "light") {
    return (
      <svg
        aria-hidden="true"
        fill="currentColor"
        height="20"
        viewBox="0 0 24 24"
        width="20"
      >
        <path d="M20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12zM12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6m0-10c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4" />
      </svg>
    );
  }

  if (mode === "dark") {
    return (
      <svg
        aria-hidden="true"
        fill="currentColor"
        height="20"
        viewBox="0 0 24 24"
        width="20"
      >
        <path d="M20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12zM12 18c-.89 0-1.74-.2-2.5-.55C11.56 16.5 13 14.42 13 12s-1.44-4.5-3.5-5.45C10.26 6.2 11.11 6 12 6c3.31 0 6 2.69 6 6s-2.69 6-6 6" />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      fill="currentColor"
      height="20"
      viewBox="0 0 24 24"
      width="20"
    >
      <path d="M10.85 12.65h2.3L12 9zM20 8.69V4h-4.69L12 .69 8.69 4H4v4.69L.69 12 4 15.31V20h4.69L12 23.31 15.31 20H20v-4.69L23.31 12zM14.3 16l-.7-2h-3.2l-.7 2H7.8L11 7h2l3.2 9z" />
    </svg>
  );
};

export const ThemeSwitcher = ({ className = "" }: ThemeSwitcherProps) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => getInitialThemeMode());
  const { t } = useI18n();

  useEffect(() => {
    applyThemeMode(themeMode);
    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  const ariaLabelByMode: Record<ThemeMode, string> = {
    dark: t("theme.label.dark"),
    light: t("theme.label.light"),
    system: t("theme.label.system"),
  };

  return (
    <button
      aria-label={ariaLabelByMode[themeMode]}
      className={`theme-switcher ${className}`.trim()}
      onClick={() => setThemeMode(getNextThemeMode(themeMode))}
      type="button"
    >
      <ThemeIcon mode={themeMode} />
    </button>
  );
};
